import React, { useState } from "react";
import styled from "styled-components";
import { useTheme } from "@mui/material/styles";
import {
  ToggleButton,
  ToggleButtonGroup,
} from "Components/Base/Inputs/ToggleButton";
import { Box } from "Components/Base/Layout/Box";

/**
 * Grupperade knappar för filtrering
 *
 * @example
 *  const buttons = [
 *    { value: this.l("all"), filter: CONSTANTS.PROGRESS_ALL },
 *    {
 *      value: `${this.l("lefttodo")} (${leftToDo})`,
 *      filter: CONSTANTS.FILTER_TODO,
 *    }, ...
 *
 *  return (
 *  <Filter onSelectFilter={onSelectFilter} buttons={buttons} />
 *  )
 * @component
 * @param {function} onSelectFilter - Funktion som anropas när en knapp väljs, skickar med filtret
 * @param {array} buttons - Array med objekt som innehåller knapparnas värden och filter
 * @param {int} value - The currently selected value within the group
 * @param {string} className - CSS-klasser från styled-components
 * @returns {JSX.Element}
 */

const FilterButtons = ({
  onSelectFilter = () => ({}),
  value = 0,
  buttons = [],
  className,
}) => {
  const [currentIndex, setCurrentIndex] = useState(value);
  const theme = useTheme();

  const handleChange = (_, newSelected) => {
    // hinder unselecting
    if (newSelected !== null) {
      setCurrentIndex(newSelected);
      onSelectFilter(buttons[newSelected].filter);
    }
  };

  return (
    <StyledToggleButtonGroup
      exclusive
      value={currentIndex}
      onChange={handleChange}
      className={className}
      theme={theme}
    >
      {buttons.map((btn, i) => (
        <StyledToggleButton theme={theme} value={i} key={btn.value}>
          <Box sx={{ fontWeight: "bold" }}>{btn.value}</Box>
        </StyledToggleButton>
      ))}
    </StyledToggleButtonGroup>
  );
};

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  .MuiToggleButtonGroup-groupedHorizontal:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
  .MuiToggleButtonGroup-groupedHorizontal:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  .MuiToggleButtonGroup-groupedHorizontal:not(:first-child) {
    margin-left: 0px;
  }

  .MuiToggleButton-root {
    border: 1px solid var(--colorDivider);
  }

  .MuiToggleButton-root.Mui-selected {
    border: 1px solid ${({ theme }) => theme.palette.primary.main} !important;
    color: ${({ theme }) => theme.palette.primary.main};
  }

  .MuiToggleButton-root,
  .MuiToggleButton-root.Mui-selected {
    background-color: ${({ theme }) => theme.palette.main};
  }

  .Mui-selected .MuiToggleButton-label {
    color: ${({ theme }) => theme.palette.primary.main};
  }

  .MuiToggleButton-root:hover {
    background-color: var(--colorToggleButtonHover) !important;
  }

  .MuiToggleButton-root.Mui-selected:hover {
    background-color: ${({ theme }) => theme.palette.primary.light};
  }
`;

const StyledToggleButton = styled(ToggleButton)`
  transform: unset !important; /* override base.css for button */
  :focus {
    outline: none !important; /* override base.css for button */
  }
`;

export default FilterButtons;
